import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Alert,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { Dropzone, MIME_TYPES, type FileRejection } from '@mantine/dropzone';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconFileUpload } from '@tabler/icons-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { AssignmentCascade } from '../../components/emissionReports/AssignmentCascade';
import { emissionReportImportsApi } from '../../services/emissionReportImportsApi';
import type { PagedCollection } from '../../services/hydraCollection';
import { plantsApi } from '../../services/plantsApi';
import { getApiErrorMessage } from '../../utils/errorHandling';
import type { EmissionReportImport, Plant } from '../../types/entities';
import { ImportRulesPanel } from './ImportRulesPanel';
import { assignmentLabel, rowActions, statusHint } from './emissionReportRow';
import {
  ALL_STATUSES,
  STATUS_FILTER_OPTIONS,
  filterStateToSearchParams,
  pageCount,
  parseFilterParams,
  parseStatusValue,
  toQueryFilters,
  yearFilterOptions,
  type EmissionReportsFilterState,
} from './emissionReportsFilterUrl';

/**
 * Phase 35, Sketch 019 Variante A4: Es gibt keinen Arbeitsbereich neben der Liste -- es gibt nur
 * die Liste. Eine abgelegte Datei wird sofort ein Eintrag (D-01) und vervollstaendigt sich in
 * der eigenen Zeile.
 *
 * Der Stapel ist ein Stapel beim ABLEGEN, nicht beim UEBERNEHMEN: mehrere Dateien auf einmal
 * hineinzuziehen bleibt bequem, hochgeladen wird je Datei ein eigener Request (D-08), und einen
 * Sammelknopf gibt es bewusst nicht (Sketch 019, Runde 2 -- ein Bericht bringt rund 130 Werte,
 * wer bei dreien alles auf einmal uebernimmt, kontrolliert nicht mehr).
 *
 * Offene Zeilen zeigen ausdruecklich KEINE Ergebniszahlen ("6 Messungen", "22 von 22 Feldern") --
 * das liess den Bericht fertig aussehen und die Vorschau wie eine Formalie (Sketch 019, Runde 2).
 */

/** D-10: dieselbe Obergrenze wie im Backend -- die Dropzone spart nur den vergeblichen Request. */
const MAX_UPLOAD_BYTES = 10 * 1024 ** 2;

/**
 * react-dropzone meldet seine Ablehnungen mit englischen Standardtexten; die zwei Faelle, die
 * unsere Beschraenkungen ueberhaupt ausloesen koennen, bekommen hier ihren deutschen Satz.
 */
function rejectionMessage(rejection: FileRejection): string {
  const codes = rejection.errors.map((error) => error.code);
  if (codes.includes('file-too-large')) {
    return `${rejection.file.name}: Die Datei ist größer als 10 MB.`;
  }
  if (codes.includes('file-invalid-type')) {
    return `${rejection.file.name}: Nur .docx-Dateien der Anlage 1 können eingelesen werden.`;
  }
  return `${rejection.file.name}: Die Datei wurde nicht angenommen.`;
}

/**
 * `YYYY-MM-DD` -> `DD.MM.YYYY`, rein ueber Zeichenketten. Bewusst OHNE `new Date(iso)`: das
 * ergibt Mitternacht UTC, und in einer Zone mit negativem Offset zeigt die Ausgabe dann den
 * Vortag (dieselbe Falle wie in `utils/reportPeriod.ts`).
 */
function formatIsoDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  return day && month && year ? `${day}.${month}.${year}` : iso;
}

/** Der Messzeitraum, wie ihn die offene Zeile traegt -- ein einzelner Tag steht nur einmal da. */
function formatMeasurementRange(entry: EmissionReportImport): string | null {
  const { measurementRangeStart: start, measurementRangeEnd: end } = entry;
  if (!start && !end) {
    return null;
  }
  if (start && end && start !== end) {
    return `Messzeitraum ${formatIsoDate(start)} – ${formatIsoDate(end)}`;
  }
  return `Messzeitraum ${formatIsoDate((start ?? end) as string)}`;
}

/** Zeitstempel des Hochladens; hier ist `Date` richtig -- der Wert traegt Zeitzoneninformation. */
function formatUploadedAt(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function EmissionReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilterParams(searchParams), [searchParams]);

  const [collection, setCollection] = useState<PagedCollection<EmissionReportImport> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Name der gerade laufenden Datei -- der Ladezustand nennt sie, weil beim Ablegen mehrerer
  // Dateien sonst nicht erkennbar ist, wie weit der Stapel ist.
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  // Werke mit mindestens einem Ofen, an dem der VDZ misst -- serverseitig gefiltert, einmal je
  // Seitenaufruf, gemeinsam fuer alle offenen Zeilen.
  const [importPlants, setImportPlants] = useState<Plant[]>([]);
  const [importPlantsLoading, setImportPlantsLoading] = useState(true);
  // An welchen Zeilen gerade gearbeitet wird (`form.isDirty()` der jeweiligen Kaskade).
  const [rowDirty, setRowDirty] = useState<Record<number, boolean>>({});

  const loadEntries = useCallback(async (state: EmissionReportsFilterState): Promise<void> => {
    setLoading(true);
    try {
      setCollection(await emissionReportImportsApi.getAll(toQueryFilters(state)));
      setError(null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Die Berichte konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadEntries(filters);
  }, [filters, loadEntries]);

  /**
   * Die Werksliste der Kaskade: nur Werke mit mindestens einem Ofen ohne eigenes Anlegerecht
   * (`kilns.allowsOwnMeasurements=false`, serverseitig). Einmal je Seitenaufruf, nicht je Zeile.
   */
  useEffect(() => {
    void (async () => {
      try {
        setImportPlants(await plantsApi.getWithVdzMeasuredKilns());
      } catch {
        // Still: die Kaskade zeigt dann ein leeres, gesperrtes Werksfeld statt eines Ladefehlers
        // der ganzen Seite. Die Liste selbst bleibt benutzbar.
      } finally {
        setImportPlantsLoading(false);
      }
    })();
  }, []);

  const handleRowDirtyChange = useCallback((entryId: number, dirty: boolean): void => {
    setRowDirty((current) =>
      current[entryId] === dirty ? current : { ...current, [entryId]: dirty }
    );
  }, []);

  function updateFilters(next: Partial<EmissionReportsFilterState>): void {
    setSearchParams(filterStateToSearchParams({ ...filters, ...next }));
  }

  /**
   * D-15/D-16: Verwerfen fragt vorher nach und nennt dabei die Datei. Uebernommene Eintraege
   * bieten den Knopf gar nicht erst an; die verbindliche Regel steht im Backend, ein 409 von
   * dort landet als Meldung im Bild.
   */
  function confirmDiscard(entry: EmissionReportImport): void {
    modals.openConfirmModal({
      title: 'Eintrag verwerfen',
      children: (
        <Text size="sm">
          „{entry.originalFilename}“ aus der Liste entfernen? Vorhandene Messungen an der Meldung
          bleiben davon unberührt.
        </Text>
      ),
      labels: { confirm: 'Verwerfen', cancel: 'Abbrechen' },
      confirmProps: { color: 'red' },
      onConfirm: () => void discardEntry(entry),
    });
  }

  async function discardEntry(entry: EmissionReportImport): Promise<void> {
    try {
      await emissionReportImportsApi.remove(entry.id);
      // War es die letzte Zeile einer hinteren Seite, waere die Seite danach leer.
      const nextPage = entries.length === 1 && filters.page > 1 ? filters.page - 1 : filters.page;
      if (nextPage !== filters.page) {
        updateFilters({ page: nextPage });
      } else {
        await loadEntries(filters);
      }
    } catch (err) {
      notifications.show({
        title: 'Eintrag nicht verworfen',
        message: getApiErrorMessage(err, 'Der Eintrag konnte nicht entfernt werden.'),
        color: 'red',
      });
    }
  }

  /**
   * D-08: ein Request je Datei, nacheinander. Bewusst sequenziell statt `Promise.all` -- der
   * Parser laeuft synchron im Request (rund 112 ms je Bericht), und ein gleichzeitig
   * abgeschickter Stapel wuerde nur die Reihenfolge der Liste verwuerfeln.
   *
   * D-09a: Eine abgelehnte Datei erzeugt eine Fehlermeldung und KEINE Zeile. Der Gegenfall
   * D-09b (gueltige Datei, Parser bricht ab) kommt aus dem Erfolgspfad des Requests und ist
   * eine ganz normale Zeile mit dem Stand "nicht lesbar".
   *
   * Nach JEDER angenommenen Datei wird die erste Seite neu vom Server geholt, statt die Antwort
   * vorne an die geladene Seite zu haengen: die Zeile erscheint genauso sofort, aber Zaehler
   * (`hydra:totalItems`) und Seitenzahl bleiben die des Servers -- nichts wird im Browser
   * hochgezaehlt.
   */
  async function handleDrop(files: File[]): Promise<void> {
    const target: EmissionReportsFilterState = { ...filters, page: 1 };
    for (const file of files) {
      setUploadingName(file.name);
      try {
        await emissionReportImportsApi.upload(file);
        await loadEntries(target);
      } catch (err) {
        notifications.show({
          title: 'Datei nicht eingelesen',
          message: `${file.name}: ${getApiErrorMessage(err, 'Die Datei konnte nicht gelesen werden.')}`,
          color: 'red',
        });
      }
    }
    setUploadingName(null);
    if (filters.page !== 1) {
      updateFilters({ page: 1 });
    }
  }

  function handleReject(rejections: FileRejection[]): void {
    for (const rejection of rejections) {
      notifications.show({
        title: 'Datei nicht angenommen',
        message: rejectionMessage(rejection),
        color: 'red',
      });
    }
  }

  const entries = collection?.['hydra:member'] ?? [];
  // D-17: der Zaehler ist das SERVERSEITIGE Aggregat, nicht die Laenge der geladenen Seite.
  const totalItems = collection?.['hydra:totalItems'] ?? 0;
  // Punkt 11 (IN-07): die Seitenzahl kommt aus `collection.lastPage` (server-seitig, aus
  // `view.last`), nicht mehr aus `totalItems` geteilt durch eine kopierte Seitengroesse.
  const totalPages = pageCount(collection);

  return (
    <Stack gap="lg">
      <PageHeader
        title="Emissionsberichte"
        description="Jeder abgelegte Bericht ist ab dem ersten Moment ein Eintrag."
      />

      <ImportRulesPanel />

      {/*
        Die Dropzone ist ausdruecklich KEIN @mantine/form-Feld: sie haelt keinen Formularwert und
        keinen Validierungszustand, `onDrop` loest unmittelbar den Upload aus. Die Projektregel
        "jedes Formular laeuft ueber @mantine/form" greift hier nicht, weil es kein Formular gibt.
      */}
      <Dropzone
        multiple
        accept={[MIME_TYPES.docx]}
        maxSize={MAX_UPLOAD_BYTES}
        loading={uploadingName !== null}
        onDrop={handleDrop}
        onReject={handleReject}
      >
        <Group justify="center" gap="sm" style={{ pointerEvents: 'none' }}>
          <IconFileUpload size={22} stroke={1.5} color="var(--mantine-color-vdzGray-6)" />
          <Text size="sm">
            <Text span fw={600}>
              Berichte ablegen
            </Text>{' '}
            — .docx der Anlage 1, auch mehrere
          </Text>
        </Group>
      </Dropzone>

      {uploadingName !== null && (
        <Text size="sm" c="dimmed">
          Wird eingelesen: {uploadingName}
        </Text>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {error}
        </Alert>
      )}

      <Card padding={0} radius="md" withBorder>
        <Group
          justify="space-between"
          wrap="nowrap"
          px="md"
          py="sm"
          bg="var(--mantine-color-vdzGray-0)"
        >
          <Group gap="sm" wrap="nowrap" align="baseline">
            <Title order={2} size="h4">
              Alle Berichte
            </Title>
            <Text size="sm" c="dimmed">
              {totalItems} {totalItems === 1 ? 'Eintrag' : 'Einträge'}
            </Text>
          </Group>
          <Group gap="sm" wrap="nowrap">
            <Select
              aria-label="Stand"
              data={STATUS_FILTER_OPTIONS}
              value={filters.status ?? ALL_STATUSES}
              onChange={(value) =>
                updateFilters({ status: parseStatusValue(value), page: 1 })
              }
              allowDeselect={false}
              w={180}
            />
            <Select
              aria-label="Jahr"
              data={yearFilterOptions().map(String)}
              value={String(filters.year)}
              onChange={(value) =>
                updateFilters({ year: Number(value) || filters.year, page: 1 })
              }
              allowDeselect={false}
              w={110}
            />
          </Group>
        </Group>

        {loading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : entries.length === 0 ? (
          <Text c="dimmed" size="sm" px="md" py="lg">
            Für diese Auswahl gibt es keine Berichte.
          </Text>
        ) : (
          entries.map((entry) => {
            // Die Zeile entscheidet nichts selbst -- Anzeigename, Zusatztext und Aktionen
            // kommen aus `emissionReportRow.ts` (D-13/D-14/D-15).
            // D-13: Werk-, Ofen- und Meldungsname kommen als eingebettete Verweise mit der
            // Zeile mit -- die Seite holt dafuer keine Stammdaten mehr nach.
            const assignment = assignmentLabel(entry);
            // Der D-14-Satz haengt an "Werk und Ofen stehen, die Meldung nicht" -- dieselbe
            // Datenlage entsteht mitten in der Kaskade. Wird an der Zeile gerade gearbeitet,
            // schweigt der Hinweis, statt "bitte neu zuordnen" zu rufen, waehrend genau das
            // gerade passiert (offener Punkt aus Plan 35-09).
            const hint = rowDirty[entry.id] === true ? '' : statusHint(entry);

            return (
              <Stack
                key={entry.id}
                gap="sm"
                px="md"
                py="sm"
                style={{ borderTop: '1px solid var(--mantine-color-vdzGray-2)' }}
              >
                <Group justify="space-between" wrap="nowrap" gap="md">
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                    {entry.status === 'accepted' && (
                      <IconCheck size={16} color="var(--mantine-color-green-7)" />
                    )}
                    <Text size="sm" fw={500}>
                      {entry.originalFilename}
                    </Text>
                    {entry.status === 'accepted' && assignment !== null && (
                      <Text size="sm" c="dimmed">
                        {assignment}
                      </Text>
                    )}
                    {/* Offene Zeilen tragen den Messzeitraum und sonst nichts -- keine
                        Ergebniszahlen, die den Bericht fertig aussehen liessen. */}
                    {entry.status === 'open' && hint === '' && (
                      <Text size="sm" c="dimmed">
                        {formatMeasurementRange(entry)}
                      </Text>
                    )}
                    {hint !== '' && (
                      <Text size="sm" c={entry.status === 'unreadable' ? 'red.7' : 'orange.7'}>
                        {hint}
                      </Text>
                    )}
                  </Group>
                  <Group gap="md" wrap="nowrap">
                    {entry.status === 'accepted' && entry.resultData && (
                      <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                        {entry.resultData.measurements.length} Messungen
                      </Text>
                    )}
                    <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      {formatUploadedAt(entry.uploadedAt)}
                      {entry.uploadedByName ? `, ${entry.uploadedByName}` : ''}
                    </Text>
                    {/* Offene Zeilen tragen ihre beiden Knoepfe in der Kaskade darunter -- dort
                        haengt "Vorschau" am Formularzustand statt am gespeicherten Eintrag. */}
                    <Group gap="xs" wrap="nowrap">
                      {(entry.status === 'open' ? [] : rowActions(entry)).map((action) =>
                        action.target === null ? (
                          <Button
                            key={action.key}
                            size="xs"
                            variant="default"
                            disabled={action.disabled}
                            title={action.disabledReason}
                            onClick={() => confirmDiscard(entry)}
                          >
                            {action.label}
                          </Button>
                        ) : (
                          <Button
                            key={action.key}
                            size="xs"
                            variant="default"
                            component={Link}
                            to={action.target}
                            disabled={action.disabled}
                            title={action.disabledReason}
                          >
                            {action.label}
                          </Button>
                        )
                      )}
                    </Group>
                  </Group>
                </Group>

                {entry.status === 'open' && (
                  <AssignmentCascade
                    entry={entry}
                    plants={importPlants}
                    plantsLoading={importPlantsLoading}
                    onDirtyChange={handleRowDirtyChange}
                    onDiscard={confirmDiscard}
                  />
                )}
              </Stack>
            );
          })
        )}
      </Card>

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination
            total={totalPages}
            value={filters.page}
            onChange={(page) => updateFilters({ page })}
          />
        </Group>
      )}
    </Stack>
  );
}
